import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Menu, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { LanguageSwitch } from "@/components/LanguageSwitch";

export function LandingNav() {
  const { t } = useTranslation("landing");
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const NAV_LINKS = [
    { label: t("nav.platform"), href: "#platform" },
    { label: t("nav.modules"), href: "#modules" },
    { label: t("nav.pricing"), href: "#pricing" },
    { label: t("nav.about"), href: "/about", isRoute: true },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleAnchorClick = (href: string) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-500",
        scrolled
          ? "bg-background/90 backdrop-blur-xl border-b border-border/30"
          : "bg-transparent"
      )}
    >
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img src="/nodistar-logo.png" alt="NODISTAR*" className="h-7 w-auto object-contain" />
        </Link>

        {/* Desktop */}
        <nav className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map((link) =>
            (link as any).isRoute ? (
              <Link
                key={link.href}
                to={link.href}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {link.label}
              </Link>
            ) : (
              <button
                key={link.href}
                onClick={() => handleAnchorClick(link.href)}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {link.label}
              </button>
            )
          )}
          <div className="flex items-center gap-3 ml-4">
            <LanguageSwitch />
            <Button variant="ghost" size="sm" className="text-sm" asChild>
              <Link to="/auth">{t("nav.login")}</Link>
            </Button>
            <Button size="sm" className="text-sm" asChild>
              <Link to="/auth?tab=signup">{t("nav.startFree")}</Link>
            </Button>
          </div>
        </nav>

        {/* Mobile toggle */}
        <button
          className="md:hidden text-foreground"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
        >
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-background/95 backdrop-blur-xl border-b border-border/30 px-6 py-6 space-y-1">
          {NAV_LINKS.map((link) =>
            (link as any).isRoute ? (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setMobileOpen(false)}
                className="block text-base text-muted-foreground hover:text-foreground py-3"
              >
                {link.label}
              </Link>
            ) : (
              <button
                key={link.href}
                onClick={() => handleAnchorClick(link.href)}
                className="block w-full text-left text-base text-muted-foreground hover:text-foreground py-3"
              >
                {link.label}
              </button>
            )
          )}
          <div className="flex items-center gap-3 pt-4">
            <LanguageSwitch />
          </div>
          <div className="flex gap-3 pt-2">
            <Button variant="ghost" size="sm" asChild className="flex-1">
              <Link to="/auth">{t("nav.login")}</Link>
            </Button>
            <Button size="sm" asChild className="flex-1">
              <Link to="/auth?tab=signup">{t("nav.startFree")}</Link>
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
